import { ReactNode } from "react";
import { Box } from "@mui/material";
import { Tag, Tabs } from 'antd';
import Table, { ColumnsType } from 'antd/es/table';
import formatNumber from '../../../Functions/Traitement/FormatNumber';

interface Tab9Props {
    form?: any;
    dataStats?: any;
}

const fmtDate = (v: any) => {
    if (!v) return '';
    const s = String(v);
    return s.length >= 10 ? (new Date(s.substring(0, 10))).toLocaleDateString('fr') : s;
};

const statutColor = (s: any): string => {
    switch (String(s ?? '').toUpperCase()) {
        case 'ACTIVE': case 'VALIDEE': case 'PAYEE': case '04': return 'green';
        case 'EN_ATTENTE': case '02': return 'gold';
        case 'IMPAYEE': case 'SUSPENDUE': case 'REJETEE': return 'red';
        case 'TERMINE': case 'CLOTUREE': return 'default';
        default: return 'blue';
    }
};

// ---------- SafeCare : souscriptions ----------
const souscriptionColumns: ColumnsType<any> = [
    { title: 'N° Souscription', dataIndex: 'IdSouscription', ellipsis: true, width: 110 },
    { title: 'Produit', dataIndex: 'NomProduit', ellipsis: true, width: 150 },
    { title: 'Version', dataIndex: 'NumeroVersion', ellipsis: true, width: 70, align: 'center' },
    { title: 'Compte débité', dataIndex: 'NumCptDebit', ellipsis: true, width: 170 },
    { title: 'Fréquence', dataIndex: 'CodeFrequence', ellipsis: true, width: 80, align: 'center' },
    { title: 'Cotisation', dataIndex: 'MontantCotisation', ellipsis: true, width: 110, align: 'right', render: (v: any) => formatNumber(v) },
    { title: 'Total dû', dataIndex: 'TotalCotisations', ellipsis: true, width: 110, align: 'right', render: (v: any) => formatNumber(v) },
    { title: 'Total payé', dataIndex: 'TotalCotisationsPayees', ellipsis: true, width: 110, align: 'right', render: (v: any) => formatNumber(v) },
    {
        title: 'Éch. impayées', dataIndex: 'NbEcheancesImpayees', ellipsis: true, width: 100, align: 'center',
        render: (v: any) => (v > 0 ? <Tag color='red'>{v}</Tag> : <Tag color='green'>0</Tag>),
    },
    { title: 'Date effet', dataIndex: 'DateEffet', ellipsis: true, width: 100, render: (v: any) => fmtDate(v) },
    { title: 'Date fin', dataIndex: 'DateFin', ellipsis: true, width: 100, render: (v: any) => fmtDate(v) },
    { title: 'Statut', dataIndex: 'Statut', ellipsis: true, width: 110, render: (v: any) => <Tag color={statutColor(v)}>{v}</Tag> },
];

// ---------- SafeCare : demandes d'assistance ----------
const assistanceColumns: ColumnsType<any> = [
    { title: 'N° Demande', dataIndex: 'IdDemandeAssistance', ellipsis: true, width: 100 },
    { title: 'Souscription', dataIndex: 'IdSouscription', ellipsis: true, width: 90, align: 'center' },
    { title: 'Événement', dataIndex: 'IdEvenement', ellipsis: true, width: 80, align: 'center' },
    { title: 'Date sinistre', dataIndex: 'DateSinistre', ellipsis: true, width: 100, render: (v: any) => fmtDate(v) },
    { title: 'Description', dataIndex: 'Description', ellipsis: true, width: 180 },
    { title: 'Montant demandé', dataIndex: 'MontantDemande', ellipsis: true, width: 120, align: 'right', render: (v: any) => formatNumber(v) },
    { title: 'Montant indemnisé', dataIndex: 'MontantIndemnise', ellipsis: true, width: 120, align: 'right', render: (v: any) => (v != null ? formatNumber(v) : '-') },
    { title: 'Date paiement', dataIndex: 'DatePaiementIndemnisation', ellipsis: true, width: 110, render: (v: any) => fmtDate(v) },
    { title: 'Statut', dataIndex: 'Statut', ellipsis: true, width: 110, render: (v: any) => <Tag color={statutColor(v)}>{v}</Tag> },
    { title: 'Motif rejet', dataIndex: 'MotifRejet', ellipsis: true, width: 130 },
];

// ---------- SafeCare : demandes de souscription ----------
const demandeSouscriptionColumns: ColumnsType<any> = [
    { title: 'N° Demande', dataIndex: 'IdDemande', ellipsis: true, width: 100 },
    { title: 'Produit', dataIndex: 'NomProduit', ellipsis: true, width: 150 },
    { title: 'Version', dataIndex: 'NumeroVersion', ellipsis: true, width: 70, align: 'center' },
    { title: 'Compte débité', dataIndex: 'NumCptDebit', ellipsis: true, width: 170 },
    { title: 'Fréquence', dataIndex: 'CodeFrequence', ellipsis: true, width: 80, align: 'center' },
    { title: 'Cotisation', dataIndex: 'MontantCotisation', ellipsis: true, width: 110, align: 'right', render: (v: any) => formatNumber(v) },
    { title: 'Effet souhaité', dataIndex: 'DateEffetSouhaitee', ellipsis: true, width: 100, render: (v: any) => fmtDate(v) },
    { title: 'Date demande', dataIndex: 'DatCre', ellipsis: true, width: 100, render: (v: any) => fmtDate(v) },
    { title: 'Statut', dataIndex: 'Statut', ellipsis: true, width: 110, render: (v: any) => <Tag color={statutColor(v)}>{v}</Tag> },
    { title: 'Motif rejet', dataIndex: 'MotifRejet', ellipsis: true, width: 130 },
];

// ---------- Bons de caisse ----------
const bdcColumns: ColumnsType<any> = [
    { title: 'N° BDC', dataIndex: 'NUMBDC', ellipsis: true, width: 110 },
    { title: 'Référence', dataIndex: 'REFBDC', ellipsis: true, width: 100 },
    { title: 'Type', dataIndex: 'TYPBDC', ellipsis: true, width: 90 },
    { title: 'Montant', dataIndex: 'MONTANT', ellipsis: true, width: 120, align: 'right', render: (v: any) => formatNumber(v) },
    { title: 'Taux (%)', dataIndex: 'TXINT', ellipsis: true, width: 80, align: 'center' },
    { title: 'Intérêts', dataIndex: 'MTNINT', ellipsis: true, width: 110, align: 'right', render: (v: any) => formatNumber(v) },
    { title: 'Net à payer', dataIndex: 'MTNNETAPAYER', ellipsis: true, width: 110, align: 'right', render: (v: any) => formatNumber(v) },
    { title: 'Durée (j)', dataIndex: 'DUREE', ellipsis: true, width: 70, align: 'center' },
    { title: 'Date début', dataIndex: 'DATDEBUT', ellipsis: true, width: 100, render: (v: any) => fmtDate(v) },
    { title: 'Date fin', dataIndex: 'DATFIN', ellipsis: true, width: 100, render: (v: any) => fmtDate(v) },
    { title: 'Compte dépôt', dataIndex: 'CPTDEPOT', ellipsis: true, width: 160 },
    { title: 'État', dataIndex: 'CDETAT', ellipsis: true, width: 80, render: (v: any) => <Tag color={statutColor(v)}>{v}</Tag> },
];

// ---------- Demandes d'interruption BDC ----------
const interruptionColumns: ColumnsType<any> = [
    { title: 'N° Demande', dataIndex: 'NUMDEM', ellipsis: true, width: 130 },
    { title: 'N° BDC', dataIndex: 'NUMBDC', ellipsis: true, width: 110 },
    { title: 'Motif', dataIndex: 'LIBMOTIF', ellipsis: true, width: 140 },
    { title: 'Montant', dataIndex: 'MONTANT', ellipsis: true, width: 110, align: 'right', render: (v: any) => formatNumber(v) },
    { title: 'Pénalité', dataIndex: 'PENALITE', ellipsis: true, width: 100, align: 'right', render: (v: any) => formatNumber(v) },
    { title: 'Intérêts dus', dataIndex: 'INTDUS', ellipsis: true, width: 100, align: 'right', render: (v: any) => formatNumber(v) },
    { title: 'Net client', dataIndex: 'NETCLIENT', ellipsis: true, width: 110, align: 'right', render: (v: any) => formatNumber(v) },
    { title: 'Date demande', dataIndex: 'DATDEM', ellipsis: true, width: 100, render: (v: any) => fmtDate(v) },
    { title: 'Date interruption', dataIndex: 'DATINTERUP', ellipsis: true, width: 110, render: (v: any) => fmtDate(v) },
    { title: 'État', dataIndex: 'CDETAT', ellipsis: true, width: 80, render: (v: any) => <Tag color={statutColor(v)}>{v}</Tag> },
];

const Tab9 = ({ dataStats }: Tab9Props) => {
    const souscriptions          = dataStats?.safeCareSouscriptions   ?? [];
    const demandesAssistance     = dataStats?.demandesAssistance      ?? [];
    const demandesSouscription   = dataStats?.demandesSouscription    ?? [];
    const bonsCaisse             = dataStats?.bonsCaisse              ?? [];
    const demandesInterruption   = dataStats?.demandesInterruptionBdc ?? [];

    const section = (title: string, table: ReactNode) => (
        <Box fontStyle="inherit" className='bordure1' sx={{ mb: 2 }}>
            <legend style={{ padding: '0 8px', fontWeight: 600 }}>{title}</legend>
            {table}
        </Box>
    );

    const items = [
        {
            key: 'sc',
            label: `SafeCare (${souscriptions.length + demandesSouscription.length})`,
            children: (
                <>
                    {section('Souscriptions SafeCare',
                        <Table size='small' bordered dataSource={souscriptions} columns={souscriptionColumns} style={{ width: '100%' }} scroll={{ y: 240, x: 'max-content' }} rowKey='IdSouscription' pagination={false} />)}
                    {section("Demandes d'assistance",
                        <Table size='small' bordered dataSource={demandesAssistance} columns={assistanceColumns} style={{ width: '100%' }} scroll={{ y: 200, x: 'max-content' }} rowKey='IdDemandeAssistance' pagination={false} />)}
                    {section('Demandes de souscription',
                        <Table size='small' bordered dataSource={demandesSouscription} columns={demandeSouscriptionColumns} style={{ width: '100%' }} scroll={{ y: 180, x: 'max-content' }} rowKey='IdDemande' pagination={false} />)}
                </>
            ),
        },
        {
            key: 'bdc',
            label: `Bons de caisse (${bonsCaisse.length})`,
            children: (
                <>
                    {section('Bons de caisse',
                        <Table size='small' bordered dataSource={bonsCaisse} columns={bdcColumns} style={{ width: '100%' }} scroll={{ y: 260, x: 'max-content' }} rowKey='NUMBDC' pagination={false} />)}
                    {section("Demandes d'interruption",
                        <Table size='small' bordered dataSource={demandesInterruption} columns={interruptionColumns} style={{ width: '100%' }} scroll={{ y: 200, x: 'max-content' }} rowKey='NUMDEM' pagination={false} />)}
                </>
            ),
        },
    ];

    return (
        <div className='tabItem'>
            <div>
                <Box display={'flex'} fontStyle="inherit" width={"85%"} position={"absolute"}>
                    <Box fontStyle="inherit" width="100%">
                        <Tabs size='small' type='card' items={items} />
                    </Box>
                </Box>
            </div>
        </div>
    );
}

export default Tab9;
